import { AbsgArrayHelper, LogService } from '@absg/angular-common';
import { AccessPoint } from '@absg/api-station-management';
import { AccessPointDoorState, ModbusAccessPointNotification, StationManagementPermission } from '@absg/nim-client-shared';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AccessPointService } from 'core.services/access-point/access-point.service';
import { LockAccessService } from 'core.services/access-point/lock-access.service';
import { StationInfoService } from 'core.services/station-info.service';
import { BehaviorSubject } from 'rxjs';
import { filter, finalize, take, tap } from 'rxjs/operators';
import { FailedDoorStatus } from 'shared.enums/failed-door-status.enum';
import { FailedDoor } from 'shared.models/failed-door.model';
import { SubSink } from 'subsink';

@Component({
  selector: '[nc-resolve-failed-door]',
  templateUrl: './resolve-failed-door.component.html',
  standalone: false,
})
export class ResolveFailedDoorComponent implements OnInit, OnDestroy {
  isOpenModal = new BehaviorSubject(false);
  selectedFailedDoor: FailedDoor = null;
  failedAccessPoints: FailedDoor[] = [];
  instructionHeaderText = 'Instructions to Clear Failure';
  instructionBodyText1 =
    'Please open and close the highlighted door below. If you are unable to resolve a failed door, please contact the Oncology';
  instructionBodyText3 =
    'There seems to be no matching failed doors. Click Refresh to check for Failed Doors again or click Close to ignore until the door is flagged again.';
  instructionBodyText2 = 'Supply Help Desk at 1.877.224.9692.';
  showSearchSpinner$ = new BehaviorSubject(false);
  subsink = new SubSink();
  storageTabPermission = StationManagementPermission.manageStationStorage;

  constructor(
    private router: Router,
    private accessPointService: AccessPointService,
    private stationInfoService: StationInfoService,
    private lockService: LockAccessService,
    private loggingService?: LogService,
  ) {
    this.subsink.sink = this.accessPointService.isResolvedDoorOpen.pipe().subscribe(open => {
      if (open) {
        this.lockService.isResolvingDoors = true;
        this.onModalOpen();
      } else {
        this.lockService.isResolvingDoors = false;
      }
      this.isOpenModal.next(open);
    });
  }

  ngOnInit(): void {
    this.subsink.sink = this.accessPointService.resetAccessPointResponse.subscribe(response => { this.processResetAccessPointResponse(response); });
    this.subsink.sink = this.accessPointService.accessPointStateChangedNotification.subscribe(notification => { this.processAccessPointStateChangedNotification(notification); });
    this.subsink.sink = this.accessPointService.doorFailureResolved.subscribe(resolvedAccessPointId => { this.processDoorFailureResolved(resolvedAccessPointId); });
  }

  ngOnDestroy(): void {
    this.subsink.unsubscribe();
  }

  onModalOpen(): void {
    this.stationInfoService.station.asObservable()
      .pipe(
        filter((station) => station != null),
        take(1),
      ).subscribe(() => {
        this.getFailedDoors();
      });
  }

  private getFailedDoors(): void {
    this.showSearchSpinner$.next(true);
    this.accessPointService.getFailedAccessPointIds().pipe(
      take(1),
      tap((failedAccessPointIds: number[]) => {
        this.failedAccessPoints = [];
        if (failedAccessPointIds && failedAccessPointIds.length > 0) {
          AbsgArrayHelper.forEach(failedAccessPointIds, failedAccessPointId => {
            const failedAccessPoint: AccessPoint = this.stationInfoService.getAccessPointById(failedAccessPointId);
            if (failedAccessPoint) {
              const failedAccessPointViewModel: FailedDoor = new FailedDoor(failedAccessPoint.id, failedAccessPoint.name, FailedDoorStatus.Failed, 'Door Failure', false);
              this.failedAccessPoints.push(failedAccessPointViewModel);
            }
          });
        }

        if (this.failedAccessPoints && this.failedAccessPoints.length > 0) {
          this.sortFailedAccessPoints();
          this.proceedWithSelectedFailedAccessPoint(this.failedAccessPoints[0]);
        } else {
          this.loggingService.logTrace('There were not failed doors to resolve, Closing Modal');
          this.closingModalActions();
        }
      }),
      finalize(() => this.showSearchSpinner$.next(false)),
    ).subscribe();
  }

  processResetAccessPointResponse(response: ModbusAccessPointNotification): void {
    if (this.selectedFailedDoor && !response.failureFlag && this.selectedFailedDoor.accessPointId === response.accessPointId) {
      this.selectedFailedDoor.status = FailedDoorStatus.Open;
      this.selectedFailedDoor.statusMessage = 'Close Door to Continue';
    } else if (this.selectedFailedDoor && response.failureFlag && this.selectedFailedDoor.accessPointId === response.accessPointId) {
      const nextFailedAccessPoint: FailedDoor = this.findNextFailedAccessPoint();
      if (nextFailedAccessPoint) {
        this.proceedWithSelectedFailedAccessPoint(nextFailedAccessPoint);
      }
    }
  }

  processAccessPointStateChangedNotification(notification: ModbusAccessPointNotification): void {
    if (this.selectedFailedDoor && notification.doorSensorState === AccessPointDoorState.Open
      && this.selectedFailedDoor.accessPointId === notification.accessPointId) {
      this.selectedFailedDoor.status = FailedDoorStatus.Close;
    } else if (this.selectedFailedDoor && notification.doorSensorState === AccessPointDoorState.Closed
      && this.selectedFailedDoor.accessPointId === notification.accessPointId) {
      this.selectedFailedDoor.status = FailedDoorStatus.Closed;
    }
  }

  processDoorFailureResolved(resolvedAccessPointId: number): void {
    this.logTrace(`Resolved Access Point ${resolvedAccessPointId}`);
    if (this.selectedFailedDoor && this.selectedFailedDoor.accessPointId === resolvedAccessPointId) {
      this.selectedFailedDoor.status = FailedDoorStatus.Resolved;
      this.selectedFailedDoor.statusMessage = 'Failure Clear';
    }

    const nextFailedAccessPoint: FailedDoor = this.findNextFailedAccessPoint();
    if (nextFailedAccessPoint) {
      this.proceedWithSelectedFailedAccessPoint(nextFailedAccessPoint);
    } else {
      if (this.isAllFailedDoorResolved()) {
        this.logTrace('All Access points resolved');
        this.selectedFailedDoor = null;
        this.closingModalActions();
      }
    }
  }

  findNextFailedAccessPoint(): FailedDoor {
    let nextFailedAccessPoint: FailedDoor = null;
    if (this.failedAccessPoints && this.failedAccessPoints.length > 0) {
      nextFailedAccessPoint = AbsgArrayHelper.find(this.failedAccessPoints, accessPoint => accessPoint.status === FailedDoorStatus.Failed
        && !accessPoint.selected);
    }
    return nextFailedAccessPoint;
  }

  isAllFailedDoorResolved(): boolean {
    let allFailedDoorResolved = false;
    let failedAccessPoint: FailedDoor = null;
    if (this.failedAccessPoints && this.failedAccessPoints.length > 0) {
      failedAccessPoint = AbsgArrayHelper.find(this.failedAccessPoints, accessPoint => accessPoint.status !== FailedDoorStatus.Resolved);
    }
    if (!failedAccessPoint) {
      allFailedDoorResolved = true;
    }
    return allFailedDoorResolved;
  }

  proceedWithSelectedFailedAccessPoint(currentSelectedFailedAccessPoint: FailedDoor): void {
    if (currentSelectedFailedAccessPoint) {
      this.selectedFailedDoor = currentSelectedFailedAccessPoint;
      this.selectedFailedDoor.selected = true;
      this.resolveDoorFailure(this.selectedFailedDoor);
    }
  }

  resolveDoorFailure(failedDoor: FailedDoor): void {
    this.logTrace(`Resetting door ${failedDoor.name}(${failedDoor.accessPointId}) with status ${failedDoor.status}`);
    this.accessPointService.resetAccessPoint(failedDoor.accessPointId);
    failedDoor.status = FailedDoorStatus.Resolving;
  }

  isDoorInFailedState(failedDoor: FailedDoor): boolean {
    let isDoorInFailedState = false;
    if (failedDoor.status === FailedDoorStatus.Failed || failedDoor.status === FailedDoorStatus.Resolving) {
      isDoorInFailedState = true;
    }
    return isDoorInFailedState;
  }

  isDoorInTransitionState(failedDoor: FailedDoor): boolean {
    let isDoorInTransitionState = false;
    if (failedDoor.status === FailedDoorStatus.Open || failedDoor.status === FailedDoorStatus.Close
      || failedDoor.status === FailedDoorStatus.Closed) {
      isDoorInTransitionState = true;
    }
    return isDoorInTransitionState;
  }

  isDoorInResolvedState(failedDoor: FailedDoor): boolean {
    let isDoorInResolvedState = false;
    if (failedDoor.status === FailedDoorStatus.Resolved) {
      isDoorInResolvedState = true;
    }
    return isDoorInResolvedState;
  }

  getFailedDoorId(failedDoor: FailedDoor): string {
    return `failedDoor${failedDoor.accessPointId}`;
  }

  getFailedDoorInstructionId(failedDoor: FailedDoor): string {
    return `instruction${failedDoor.accessPointId}`;
  }

  sortFailedAccessPoints(): void {
    this.failedAccessPoints.sort((d1, d2) => d1.name < d2.name ? -1 : 1);
  }

  hasNoFailedDoors(): boolean {
    return this.failedAccessPoints.length > 0;
  }

  onSettingsClick(): void {
    this.router.navigate(['/station-management']);
    this.accessPointService.isResolvedDoorOpen.next(false);
  }

  refreshList(): void {
    this.loggingService.logTrace('Refresh doors called, no valid AC available to resolve.');
    this.getFailedDoors();
  }

  closeModal(): void {
    this.loggingService.logTrace('User closed the Resolved Door Modal.');
    this.closingModalActions();
  }

  private closingModalActions(): void {
    this.isOpenModal.next(false);
    this.accessPointService.isResolvedDoorOpen.next(false);
  }

  trackItem(__: number, item: FailedDoor): string {
    if (!item) { return undefined; }
    return item.name;
  }

  private logTrace(message: string): void {
    if (this.loggingService) {
      this.loggingService.logTrace(message);
    }
  }
}
